import { createChipTextureArray } from "./chipTexture";
import * as D from "./dimensions";
import {
  FLOATS_PER_VERTEX,
  MeshData,
  buildBallMesh,
  buildHeadMesh,
  buildStaticMesh,
} from "./geometry";
import { identity, rotationY, translation } from "./mat4";
import { PALETTE, encodeSrgb } from "./palette";
import { Camera, KEY_LIGHT, WheelRenderer, cameraFor } from "./scene";
import { CHIP_SHADER, SOLID_SHADER } from "./shaders";
import { SLOT_COUNT, WheelLayout, slotAngle } from "./wheelLayout";

// The WebGPU half of the wheel. Three solid draws — the bowl, the turning
// head, the ball — and then the 22 avatar chips as one instanced draw over the
// top of them.

const SAMPLE_COUNT = 4;
/** Uniform buffer offsets must be 256-byte aligned to be used as dynamic ones. */
const OBJECT_STRIDE = 256;
const OBJECT_FLOATS = OBJECT_STRIDE / 4;
const OBJECT_COUNT = 3;
const GLOBALS_SIZE = 128;
/** model + normal matrix + tint, the part of each 256-byte slot in use. */
const OBJECT_BINDING_SIZE = 144;
const CHIP_FLOATS = 4;



interface GpuMesh {
  vertices: GPUBuffer;
  indices: GPUBuffer;
  count: number;
}

function uploadMesh(device: GPUDevice, mesh: MeshData, label: string): GpuMesh {
  const vertices = device.createBuffer({
    label: `${label}-vertices`,
    size: mesh.vertices.byteLength,
    usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
  });
  device.queue.writeBuffer(vertices, 0, mesh.vertices);
  const indices = device.createBuffer({
    label: `${label}-indices`,
    size: mesh.indices.byteLength,
    usage: GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST,
  });
  device.queue.writeBuffer(indices, 0, mesh.indices);
  return { vertices, indices, count: mesh.indices.length };
}

export function isWebGpuAvailable(): boolean {
  return typeof navigator !== "undefined" && "gpu" in navigator;
}

/**
 * Sets the wheel up on the GPU. Resolves to `null` — rather than throwing —
 * on any device that can't or won't give us a WebGPU device, which is the
 * signal for the caller to fall back to the 2D painter.
 */
export async function createWebGpuRenderer(
  canvas: HTMLCanvasElement,
  layout: WheelLayout,
): Promise<WheelRenderer | null> {
  if (!isWebGpuAvailable()) return null;

  let adapter: GPUAdapter | null = null;
  let device: GPUDevice;
  try {
    adapter = await navigator.gpu.requestAdapter();
    if (!adapter) return null;
    device = await adapter.requestDevice();
  } catch {
    return null;
  }

  const context = canvas.getContext("webgpu");
  if (!context) {
    device.destroy();
    return null;
  }

  const format = navigator.gpu.getPreferredCanvasFormat();
  context.configure({ device, format, alphaMode: "opaque" });

  let lost = false;
  device.lost.then(() => {
    lost = true;
  });

  // --- meshes -------------------------------------------------------------
  const bowl = uploadMesh(device, buildStaticMesh(), "bowl");
  const head = uploadMesh(device, buildHeadMesh(layout), "head");
  const ball = uploadMesh(device, buildBallMesh(), "ball");

  // --- uniforms -----------------------------------------------------------
  const globalsBuffer = device.createBuffer({
    label: "globals",
    size: GLOBALS_SIZE,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });
  const objectBuffer = device.createBuffer({
    label: "objects",
    size: OBJECT_STRIDE * OBJECT_COUNT,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });
  const chipBuffer = device.createBuffer({
    label: "chips",
    size: SLOT_COUNT * CHIP_FLOATS * 4,
    usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
  });

  const globalsData = new Float32Array(GLOBALS_SIZE / 4);
  const objectData = new Float32Array(OBJECT_FLOATS * OBJECT_COUNT);
  const chipData = new Float32Array(SLOT_COUNT * CHIP_FLOATS);

  const globalsEntries: GPUBindGroupLayoutEntry[] = [
    {
      binding: 0,
      visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
      buffer: { type: "uniform" },
    },
  ];
  const objectEntries: GPUBindGroupLayoutEntry[] = [
    {
      binding: 0,
      visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
      buffer: { type: "uniform", hasDynamicOffset: true, minBindingSize: OBJECT_BINDING_SIZE },
    },
  ];
  const chipEntries: GPUBindGroupLayoutEntry[] = [
    { binding: 0, visibility: GPUShaderStage.FRAGMENT, sampler: { type: "filtering" } },
    {
      binding: 1,
      visibility: GPUShaderStage.FRAGMENT,
      texture: { sampleType: "float", viewDimension: "2d-array" },
    },
  ];
  const globalsLayout = device.createBindGroupLayout({ entries: globalsEntries });
  const objectLayout = device.createBindGroupLayout({ entries: objectEntries });
  const chipLayout = device.createBindGroupLayout({ entries: chipEntries });

  const globalsGroup = device.createBindGroup({
    layout: globalsLayout,
    entries: [{ binding: 0, resource: { buffer: globalsBuffer } }],
  });
  const objectGroup = device.createBindGroup({
    layout: objectLayout,
    entries: [{ binding: 0, resource: { buffer: objectBuffer, size: OBJECT_BINDING_SIZE } }],
  });

  const chipTexture = createChipTextureArray(
    device,
    layout.entries.map((entry) => ({
      emoji: entry.emoji,
      kind: entry.kind,
      label: entry.label,
    })),
  );
  const chipSampler = device.createSampler({
    magFilter: "linear",
    minFilter: "linear",
    mipmapFilter: "linear",
    addressModeU: "clamp-to-edge",
    addressModeV: "clamp-to-edge",
    maxAnisotropy: 4,
  });
  const chipGroup = device.createBindGroup({
    layout: chipLayout,
    entries: [
      { binding: 0, resource: chipSampler },
      { binding: 1, resource: chipTexture.createView() },
    ],
  });

  // --- pipelines ----------------------------------------------------------
  // position, normal, colour, (field, material)
  const solidAttributes: GPUVertexAttribute[] = [
    { shaderLocation: 0, offset: 0, format: "float32x3" },
    { shaderLocation: 1, offset: 12, format: "float32x3" },
    { shaderLocation: 2, offset: 24, format: "float32x3" },
    { shaderLocation: 3, offset: 36, format: "float32x2" },
  ];
  const solidVertexLayout: GPUVertexBufferLayout = {
    arrayStride: FLOATS_PER_VERTEX * 4,
    attributes: solidAttributes,
  };
  // One chip per instance: (angle, texture layer, scale, glow)
  const chipVertexLayout: GPUVertexBufferLayout = {
    arrayStride: CHIP_FLOATS * 4,
    stepMode: "instance",
    attributes: [{ shaderLocation: 0, offset: 0, format: "float32x4" }] as GPUVertexAttribute[],
  };

  const depthStencil: GPUDepthStencilState = {
    format: "depth24plus",
    depthWriteEnabled: true,
    depthCompare: "less",
  };

  const solidDescriptor: GPURenderPipelineDescriptor = {
    label: "solid",
    layout: device.createPipelineLayout({ bindGroupLayouts: [globalsLayout, objectLayout] }),
    vertex: {
      module: device.createShaderModule({ code: SOLID_SHADER }),
      entryPoint: "vs",
      buffers: [solidVertexLayout],
    },
    fragment: {
      module: device.createShaderModule({ code: SOLID_SHADER }),
      entryPoint: "fs",
      targets: [{ format }],
    },
    // No culling: the bowl and the pockets are open surfaces you can see the
    // inside of, and the shader flips a back face's normal for itself.
    primitive: { topology: "triangle-list", cullMode: "none" },
    depthStencil,
    multisample: { count: SAMPLE_COUNT },
  };
  const solidPipeline = device.createRenderPipeline(solidDescriptor);

  const chipTargets: GPUColorTargetState[] = [
    {
      format,
      blend: {
        color: { srcFactor: "src-alpha", dstFactor: "one-minus-src-alpha" },
        alpha: { srcFactor: "one", dstFactor: "one-minus-src-alpha" },
      },
    },
  ];
  const chipDescriptor: GPURenderPipelineDescriptor = {
    label: "chips",
    layout: device.createPipelineLayout({ bindGroupLayouts: [globalsLayout, chipLayout] }),
    vertex: {
      module: device.createShaderModule({ code: CHIP_SHADER }),
      entryPoint: "vs",
      buffers: [chipVertexLayout],
    },
    fragment: {
      module: device.createShaderModule({ code: CHIP_SHADER }),
      entryPoint: "fs",
      targets: chipTargets,
    },
    primitive: { topology: "triangle-list" },
    // The chips test against the wheel but don't write depth: they are sorted
    // back to front on the CPU each frame, and two overlapping chips have to
    // blend, not reject each other.
    depthStencil: { format: "depth24plus", depthWriteEnabled: false, depthCompare: "less" },
    multisample: { count: SAMPLE_COUNT },
  };
  const chipPipeline = device.createRenderPipeline(chipDescriptor);

  // --- per-size resources -------------------------------------------------
  let msaaTexture: GPUTexture | null = null;
  let depthTexture: GPUTexture | null = null;
  let camera: Camera = cameraFor(1);
  let width = 0;
  let height = 0;

  const clear = encodeSrgb(PALETTE.feltDeep);
  const clearValue = { r: clear[0], g: clear[1], b: clear[2], a: 1 };

  const order = Array.from({ length: SLOT_COUNT }, (_, i) => i);

  const renderer: WheelRenderer = {
    kind: "webgpu",

    resize(cssWidth, cssHeight, dpr) {
      const w = Math.max(1, Math.round(cssWidth * dpr));
      const h = Math.max(1, Math.round(cssHeight * dpr));
      if (w === width && h === height) return;
      width = w;
      height = h;
      canvas.width = w;
      canvas.height = h;
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${cssHeight}px`;

      msaaTexture?.destroy();
      depthTexture?.destroy();
      msaaTexture = device.createTexture({
        size: [w, h],
        sampleCount: SAMPLE_COUNT,
        format,
        usage: GPUTextureUsage.RENDER_ATTACHMENT,
      });
      depthTexture = device.createTexture({
        size: [w, h],
        sampleCount: SAMPLE_COUNT,
        format: "depth24plus",
        usage: GPUTextureUsage.RENDER_ATTACHMENT,
      });
      camera = cameraFor(w / h);
    },

    render(state) {
      if (lost || !msaaTexture || !depthTexture || width === 0) return;

      globalsData.set(camera.viewProj, 0);
      globalsData.set([camera.eye[0], camera.eye[1], camera.eye[2], 1], 16);
      globalsData.set([KEY_LIGHT[0], KEY_LIGHT[1], KEY_LIGHT[2], 0], 20);
      globalsData.set([state.highlight, state.glow, state.time, D.CHIP_Y], 24);
      globalsData.set([D.CHIP_RADIUS, D.CHIP_SIZE, 0, 0], 28);
      device.queue.writeBuffer(globalsBuffer, 0, globalsData);

      const write = (slot: number, model: Float32Array, normal: Float32Array, tint: number[]) => {
        const base = slot * OBJECT_FLOATS;
        objectData.set(model, base);
        objectData.set(normal, base + 16);
        objectData.set(tint, base + 32);
      };
      const still = identity();
      write(0, still, still, [1, 1, 1, 0]);
      // Negated on purpose. Everywhere else in the game a position on the
      // wheel is the angle you get from atan2(z, x) — pockets, chips, the ball
      // all agree on it — and a right-handed rotation about Y runs that angle
      // backwards. Without the sign the pockets turn one way and the avatars
      // sitting in them turn the other.
      const spin = rotationY(-state.wheelAngle);
      write(1, spin, spin, [1, 1, 1, 0]);
      const ballModel = translation(
        state.ball.radius * Math.cos(state.ball.angle),
        state.ball.y,
        state.ball.radius * Math.sin(state.ball.angle),
      );
      write(2, ballModel, still, [1, 1, 1, 0]);
      device.queue.writeBuffer(objectBuffer, 0, objectData);

      // Chips, furthest from the camera first, so the near ones blend over
      // their neighbours rather than under them.
      const angleOf = (slot: number) => slotAngle(slot) + state.wheelAngle;
      order.sort((a, b) => Math.sin(angleOf(a)) - Math.sin(angleOf(b)));
      order.forEach((slot, i) => {
        const winner = slot === state.highlight;
        chipData[i * CHIP_FLOATS] = angleOf(slot);
        chipData[i * CHIP_FLOATS + 1] = layout.slots[slot];
        chipData[i * CHIP_FLOATS + 2] = winner ? state.highlightScale : 1;
        chipData[i * CHIP_FLOATS + 3] = winner ? state.glow : 0;
      });
      device.queue.writeBuffer(chipBuffer, 0, chipData);

      const encoder = device.createCommandEncoder();
      const colorAttachments: GPURenderPassColorAttachment[] = [
        {
          view: msaaTexture.createView(),
          resolveTarget: context.getCurrentTexture().createView(),
          clearValue,
          loadOp: "clear",
          // The multisampled colour is only ever needed for the resolve.
          storeOp: "discard",
        },
      ];
      const passDescriptor: GPURenderPassDescriptor = {
        colorAttachments,
        depthStencilAttachment: {
          view: depthTexture.createView(),
          depthClearValue: 1,
          depthLoadOp: "clear",
          depthStoreOp: "discard",
        },
      };
      const pass = encoder.beginRenderPass(passDescriptor);

      pass.setPipeline(solidPipeline);
      pass.setBindGroup(0, globalsGroup);
      const meshes: Array<[GpuMesh, number]> = [
        [bowl, 0],
        [head, 1],
      ];
      if (state.ballVisible) meshes.push([ball, 2]);
      for (const [mesh, index] of meshes) {
        pass.setBindGroup(1, objectGroup, [index * OBJECT_STRIDE]);
        pass.setVertexBuffer(0, mesh.vertices);
        pass.setIndexBuffer(mesh.indices, "uint32");
        pass.drawIndexed(mesh.count);
      }

      pass.setPipeline(chipPipeline);
      pass.setBindGroup(0, globalsGroup);
      pass.setBindGroup(1, chipGroup);
      pass.setVertexBuffer(0, chipBuffer);
      pass.draw(6, SLOT_COUNT);

      pass.end();
      device.queue.submit([encoder.finish()]);
    },

    destroy() {
      msaaTexture?.destroy();
      depthTexture?.destroy();
      chipTexture.destroy();
      for (const mesh of [bowl, head, ball]) {
        mesh.vertices.destroy();
        mesh.indices.destroy();
      }
      globalsBuffer.destroy();
      objectBuffer.destroy();
      chipBuffer.destroy();
      // Deliberately not context.unconfigure(): in development React mounts
      // every component twice, so a second renderer can already have claimed
      // this canvas by the time the first one is torn down, and unconfiguring
      // would leave that one drawing into nothing. Configuring again replaces
      // the old configuration on its own.
      device.destroy();
    },
  };

  return renderer;
}
